<template>
  <div class="transaction-amount-container">
    <el-card class="box-card">
      <div slot="header" class="clearfix">
        <span style="vertical-align: sub"
          ><i class="el-icon-date"></i> Transaction & Amount Report</span
        >
      </div>
      <div>
        <div class="search-area">
          <el-radio-group v-model="projectScope" @change="tableData = []">
            <el-radio-button label="all">All</el-radio-button>
            <el-radio-button label="project">Selected Project</el-radio-button>
          </el-radio-group>

          <el-date-picker
            v-model="dateSearch"
            type="daterange"
            range-separator="To"
            start-placeholder="Start date"
            end-placeholder="End date"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
          >
          </el-date-picker>
          <el-button v-if="projectScope === 'project'" slot="append" type="primary" icon="el-icon-search" @click="getTransactionData()">Search</el-button>
          <el-button icon="el-icon-download" type="success" :loading="exporting" @click="Report()">Export</el-button>
        </div>
        <div class="note">
          <i class="el-icon-info"></i>
          Customers only. Transaction In/Out counts and amounts exclude fees.
          <span v-if="projectScope === 'all'">All = every project in the company, including archived projects. The Excel file is generated in the background and a download link (valid 24 hours) is emailed to you.</span>
        </div>

        <data-tables
          v-if="projectScope === 'project'"
          v-loading="loading"
          :data="tableData"
          border
          :page-size="20"
          :pagination-props="{ background: true, pageSizes: [20, 50, 100, 200] }">
          <el-table-column prop="from" label="From" header-align="center" align="center" width="100">
            <template slot-scope="scope">{{ formatDate(scope.row.from) }}</template>
          </el-table-column>
          <el-table-column prop="to" label="To" header-align="center" align="center" width="100">
            <template slot-scope="scope">{{ formatDate(scope.row.to) }}</template>
          </el-table-column>
          <el-table-column prop="customer_name" label="Customer Name" header-align="center" align="center" width="200"> </el-table-column>
          <el-table-column prop="member_code" label="Member Code" header-align="center" align="center" width="120"> </el-table-column>
          <el-table-column prop="bankholderID" label="BankholderID" header-align="center" align="center" width="120"> </el-table-column>
          <el-table-column prop="project_name" label="Project" header-align="center" align="center" width="200"> </el-table-column>
          <el-table-column prop="distribution_code" label="Distribution Code" header-align="center" align="center" width="140"> </el-table-column>
          <el-table-column prop="currency" label="Currency" header-align="center" align="center" width="90"> </el-table-column>
          <el-table-column prop="transaction_in" label="Transaction In" header-align="center" align="center" width="120"> </el-table-column>
          <el-table-column prop="transaction_in_amount" label="Amount" header-align="center" align="right" width="130">
            <template slot-scope="scope">{{ formatAmount(scope.row.transaction_in_amount) }}</template>
          </el-table-column>
          <el-table-column prop="transaction_out" label="Transaction Out" header-align="center" align="center" width="130"> </el-table-column>
          <el-table-column prop="transaction_out_amount" label="Amount" header-align="center" align="right" width="130">
            <template slot-scope="scope">{{ formatAmount(scope.row.transaction_out_amount) }}</template>
          </el-table-column>
          <el-table-column prop="remaining_balance" label="Remaining Balance" header-align="center" align="right" width="150">
            <template slot-scope="scope">{{ formatAmount(scope.row.remaining_balance) }}</template>
          </el-table-column>
          <el-table-column prop="account_status" label="Status" header-align="center" align="center" width="110">
            <template slot-scope="scope">
              <el-tag size="mini" effect="dark" :type="statusType(scope.row.account_status)">
                {{ scope.row.account_status }}
              </el-tag>
            </template>
          </el-table-column>
        </data-tables>

      </div>
    </el-card>
  </div>
</template>

<script>
import { getTransactionV2, getTransactionV2Excel, requestTransactionV2AllExcel } from '@/api/report'
import { currentDateTime, dateFormat, currencyFormat } from '@/utils'
import { mapGetters } from "vuex";
export default {

  props:{
    company: String,
    project: String
  },
  computed: {
    ...mapGetters(["user_project", "company_uid"]),
  },
  watch: {
      project(val, old) {
        if(this.projectScope === 'project'){
          this.getTransactionData();
        }
      }
  },
  data(){
    return{
      dateSearch: "",
      tableData: [],
      loading: false,
      exporting: false,
      projectScope: "project",
    }
  },
  methods:{
    getTransactionData(){
      const query = this.buildQuery();
      if(!query) return;
      this.loading = true
      getTransactionV2(query).then(({result, data}) => {
        this.loading = false
        if(result){
          this.tableData = data
        }
      }).catch(() => {
        this.loading = false
      });
    },
    Report(){
      const query = this.buildQuery();
      if(!query) return;
      if(this.projectScope !== 'all'){
        getTransactionV2Excel(query)
        return
      }
      this.exporting = true
      requestTransactionV2AllExcel(query).then(({result, message}) => {
        this.exporting = false
        if(result){
          this.$alert(message || "Report accepted. A download link will be emailed when ready.", "Export All Projects", {
            type: "success",
            confirmButtonText: "OK"
          })
        }else{
          this.$alert(message || "Export failed", "Error", { type: "error" })
        }
      }).catch((error) => {
        this.exporting = false
        // 401 is already handled (logout) by the request interceptor
        if(error.response && error.response.status === 401) return
        const data = error.response && error.response.data
        this.$alert((data && data.message) || error.message || "Export failed", "Error", { type: "error" })
      });
    },
    // V2 accepts exactly these params (additionalProperties: false) and only client=0
    buildQuery(){
      const company_uid = this.company || this.company_uid || ""
      const project_uid = this.projectScope === 'all' ? "all" : (this.project || this.user_project || "")
      if(!company_uid){
        this.$message.warning("Please select a company")
        return null
      }
      if(!project_uid){
        this.$message.warning("Please select a project")
        return null
      }
      return {
        company_uid,
        project_uid,
        from: this.dateSearch[0] || currentDateTime("YYYY-MM-DD"),
        to: this.dateSearch[1] || currentDateTime("YYYY-MM-DD"),
        client: "0"
      }
    },
    formatDate(value){
      return value ? dateFormat(value, "DD/MM/YYYY") : ""
    },
    formatAmount(value){
      return currencyFormat(Number(value || 0))
    },
    statusType(status){
      if(status === 'Actived') return 'success'
      if(status === 'Suspended') return 'warning'
      return 'info'
    },
  },
};
</script>

<style lang="scss">
.transaction-amount-container{
  .search-area{
    margin-bottom: 0;
    button{
      margin-left: 10px;
      margin-bottom: 16px;
    }
    .el-date-editor{
      margin-bottom: 16px;
    }
    .el-radio-group{
      margin-top: 12px;
      margin-right: 10px;
      margin-bottom: 16px;
    }
  }
  .note{
    margin-bottom: 16px;
    font-size: 12px;
    color: #909399;
  }
}
</style>
